import React from 'react'
import {Hotel, SplitBookingBundle} from '@findhotel/sapi'
import {CompactOffer} from 'components/Offer/Compact'
import {getOfferVariant} from 'components/Offer/Shared/utils'
import {SplitBookingOfferInfo} from 'components/Offer/types'
import {getCheapestTopOfferIndex} from 'utils/offers'

import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

interface MainProps {
  offerLayoutVariant?: 'CompactDefault' | 'CompactWithoutRoomName'
  isLoading?: false
  offers: SearchOffer[]
  hotelId: Hotel['objectID']
  splitBookingPosition?: number
  splitBookingOffers?: SplitBookingOfferInfo[]
  splitBookingBundle?: SplitBookingBundle
  hasSplitBookingOffer?: boolean
  anchorPrice: number
  anchorPriceTotal: number
  sourceComponent: string
  hasRoomLabels?: boolean
  showArrowCta?: boolean
  showFooter?: boolean
  showCashback?: boolean
  showCashbackLineBreak?: boolean
}

interface LoadingProps {
  isLoading: true
  offers?: never
  hotelId?: never
  splitBookingPosition?: never
  splitBookingOffers?: never
  hasSplitBookingOffer?: never
  splitBookingBundle?: never
  anchorPrice?: never
  anchorPriceTotal?: never
  sourceComponent?: never
  hasRoomLabels?: never
  showArrowCta?: never
  showFooter?: never
  showCashback?: never
  showCashbackLineBreak?: never
}

type Props = MainProps | LoadingProps

export const DividerWithMargin = () => (
  <Box marginTop="s500" marginBottom="s500">
    <Divider />
  </Box>
)

export const CompactOfferList: React.FC<MainProps | LoadingProps> = ({
  offers,
  isLoading,
  hotelId,
  anchorPrice,
  anchorPriceTotal,
  splitBookingOffers,
  splitBookingBundle,
  hasSplitBookingOffer,
  sourceComponent,
  splitBookingPosition = 0,
  hasRoomLabels = false,
  showArrowCta = true,
  showFooter = true,
  showCashback = false,
  showCashbackLineBreak = false
}) => {
  if (isLoading) {
    return (
      <Box data-id="loader" paddingTop="s500" paddingBottom="s500">
        <CompactOffer isLoading variant="CompactDefault" />
        <DividerWithMargin />
        <CompactOffer isLoading variant="CompactDefault" />
        <DividerWithMargin />
        <CompactOffer isLoading variant="CompactDefault" />
        <DividerWithMargin />
        <CompactOffer isLoading variant="CompactDefault" />
        <DividerWithMargin />
        <CompactOffer isLoading variant="CompactDefault" />
      </Box>
    )
  }

  const topOffers = offers.slice(0, splitBookingPosition)
  const restOfOffers = offers.slice(splitBookingPosition)

  return (
    <>
      {topOffers.map((offer, index) => (
        <CompactOfferListItem
          showArrowCta={showArrowCta}
          key={offer.id}
          offers={offers}
          index={index}
          hotelId={hotelId}
          anchorPrice={anchorPrice}
          anchorPriceTotal={anchorPriceTotal}
          sourceComponent={sourceComponent}
          hasRoomLabels={hasRoomLabels}
          showCashbackLineBreak={showCashbackLineBreak}
        />
      ))}
      {hasSplitBookingOffer && (
        <>
          {topOffers.length > 0 && <DividerWithMargin />}
          <CompactOffer
            isSplitBooking
            showArrowCTA={showArrowCta}
            hasTag={false}
            splitBookingOffers={splitBookingOffers}
            hasRoomLabels={true}
            hotelId={hotelId}
            variant="CompactDefault"
            anchorPrice={anchorPrice}
            anchorPriceTotal={anchorPriceTotal}
            sourceComponent={sourceComponent}
            showCashback={showCashback}
            splitBookingBundle={splitBookingBundle}
          />
          {topOffers.length === 0 && <DividerWithMargin />}
        </>
      )}
      {restOfOffers.map((offer, index) => (
        <CompactOfferListItem
          showArrowCta={showArrowCta}
          key={offer.id}
          offers={offers}
          index={splitBookingPosition + index}
          hotelId={hotelId}
          anchorPrice={anchorPrice}
          anchorPriceTotal={anchorPriceTotal}
          sourceComponent={sourceComponent}
          hasRoomLabels={hasRoomLabels}
          showCashback={showCashback}
        />
      ))}
      {showFooter && (
        <>
          <DividerWithMargin />
          <Box
            display="flex"
            padding="s300"
            justifyContent="center"
            alignItems="center"
            backgroundColor="background.neutral.primary"
          >
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              <FormattedMessageWrapper
                id="noMoreDeals"
                defaultMessage="No more deals to show"
              />
            </Text>
          </Box>
        </>
      )}
    </>
  )
}

interface CompactOfferListItemProps
  extends Omit<
    Props,
    | 'splitBookingOffers'
    | 'splitBookingPosition'
    | 'splitBookingBundle'
    | 'hasSplitBookingOffer'
  > {
  index: number
  showArrowCta: boolean
  showCashback?: boolean
  showCashbackLineBreak?: boolean
}

const CompactOfferListItem: React.FC<CompactOfferListItemProps> = ({
  offers,
  index,
  hotelId,
  anchorPrice,
  anchorPriceTotal,
  sourceComponent,
  hasRoomLabels,
  showArrowCta,
  showCashback,
  showCashbackLineBreak
}) => {
  const offer = offers[index]

  const isCheapest = Boolean(getCheapestTopOfferIndex(offers) === index)
  const isBofhOffer = getIsBofhOffer(offer)
  const offerVariant = getOfferVariant({isCheapest, isBofhOffer})

  return (
    <>
      {index > 0 && <DividerWithMargin />}
      <CompactOffer
        showArrowCTA={showArrowCta}
        hasInteraction
        variant="CompactDefault"
        offerVariant={offerVariant}
        anchorPrice={anchorPrice}
        anchorPriceTotal={anchorPriceTotal}
        offer={offer}
        hotelId={hotelId}
        hasRoomLabels={hasRoomLabels}
        offerPosition={index}
        sourceComponent={sourceComponent}
        showCashback={showCashback}
        showCashbackLineBreak={showCashbackLineBreak}
      />
    </>
  )
}
